import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Divider, Drawer, Grid, Typography, Box } from '@material-ui/core/';
import BlockIcon from '@material-ui/icons/Block';
import ManageAssetInNewTabLink from '../ManageAsset/ManageAssetInNewTabLink';
import SettingsIcon from '@material-ui/icons/Settings';
import AssetLiveStatusIndicator from '../ManageAsset/AssetLiveStatusIndicator';
import AssetJiraLink from '../Asset/AssetJiraLink';
import { getPrimarySrcFileUrl } from '../../Utils/AssetUtils';
import { productPanelFields } from '../../Constants/Products';
import { getProductUpdatedTime, getProductImgUrl } from '../../Utils/ProductUtils';
import ProductBuyable from '../Product/ProductBuyable';
import { artifactStatusMap } from '../../Constants/Files';
import { isNil, isEmpty } from 'lodash';
import CopyClipboardField from '../common/CopyClipboardField';
import ImageErrorAvatar from './ImageErrorAvatar';
import ModelRequiredAvatar from './ModelRequiredAvatar';

const useStyles = makeStyles((theme) => ({
	tempDrawerContainer: {
		width: '30em',
		padding: theme.spacing(2),
		'& svg': { verticalAlign: 'middle' },
	},
	body: { flexGrow: 2 },
	field: {
		fontWeight: '550',
	},
	avatar: {
		'& .MuiAvatar-root': {
			height: '10em',
			width: '14em',
		},
	},
}));

const FieldValues = ({ field, val }) => {
	const classes = useStyles();
	return (
		<Grid container alignItems="baseline" spacing={1}>
			<Grid item>
				<Typography variant="body1" className={classes.field}>
					{field}:
				</Typography>
			</Grid>
			<Grid item>
				<Typography variant="body2">{val}</Typography>
			</Grid>
		</Grid>
	);
};
const ProductInfo = ({ asset }) => {
	const product = asset?.product;
	let IVM = '';
	if (!isNil(product) && !isEmpty(product)) {
		IVM = `${product.itemNumber}-${product.vendorNumber}-${product.modelId}`;
	}
	return (
		<React.Fragment>
			<FieldValues field={productPanelFields.productId} val={product.omniItemId} />
			<FieldValues field={productPanelFields.productName} val={product.description} />
			<FieldValues field={productPanelFields.lastUpdatedAt} val={getProductUpdatedTime(product)} />
			<FieldValues field={productPanelFields.isBuyable} val={<ProductBuyable isBuyable={product.isBuyable} />} />
			<FieldValues field={productPanelFields.ivm} val={IVM} />
		</React.Fragment>
	);
};
const AssetInfo = ({ asset }) => {
	const liveArfts = asset.artifacts.filter((a) => a.status.toLowerCase() === artifactStatusMap.live.value);
	return (
		<React.Fragment>
			<FieldValues field={'Source Models'} val={`${asset.sourceFiles.length} File(s)`} />
			<FieldValues field={'Artifacts'} val={`${asset.artifacts.length} File(s)`} />
			<FieldValues field={'Status'} val={`${liveArfts.length} File(s) Live`} />
		</React.Fragment>
	);
};

const DrawerBody = ({ asset }) => {
	const classes = useStyles();
	const hasSrcFiles = asset.sourceFiles.length > 0;
	const AssetAvatar = () => {
		if (hasSrcFiles) {
			return <ImageErrorAvatar imgUrl={getPrimarySrcFileUrl(asset)} toolTipMsg="" />;
		}
		return <ModelRequiredAvatar imgUrl="" label="Model Needed" toolTipMsg="" />;
	};
	return (
		<Grid container spacing={2}>
			<Grid item xs={4}>
				<AssetLiveStatusIndicator asset={asset} />
			</Grid>
			<Grid item xs={6}>
				<AssetJiraLink asset={asset} hideEditBtn={true} />
			</Grid>
			<Grid item xs={12}>
				<Typography variant="body1" gutterBottom className={classes.field}>
					ASSET NAME:
				</Typography>
				<Typography variant="body2">{asset.name}</Typography>
			</Grid>
			<Grid item xs={12}>
				<FieldValues field={'Asset PPID'} val={<CopyClipboardField fieldName="Asset PPID" val={asset.id} />} />
			</Grid>
			<Grid container>
				<Grid item xs={6}>
					<Grid item xs={12} className={classes.avatar}>
						<AssetAvatar />
					</Grid>
					<Grid item xs={12}>
						<AssetInfo asset={asset} />
					</Grid>
				</Grid>
				<Grid item xs={6}>
					<Grid item xs={12} className={classes.avatar}>
						<ModelRequiredAvatar imgUrl={getProductImgUrl(asset)} label="Product Needed" toolTipMsg="" />
					</Grid>
					<Grid item xs={12}>
						<ProductInfo asset={asset} />
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

const AssetPreviewDrawer = (props) => {
	const classes = useStyles();
	const { asset } = props;
	const product = asset.product;
	// Handle close
	const handleClose = () => {
		props.setOpen(false);
	};
	const handleCancelBtnClick = () => {
		props.setOpen(false);
	};

	return (
		<Grid>
			<Drawer
				anchor="right"
				open={props.open}
				variant="temporary"
				onClose={handleClose}
				classes={{ paper: classes.tempDrawerContainer }}
			>
				<Grid className={classes.gridRoot} container spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h4">Asset Preview</Typography>
					</Grid>
					<Grid className={classes.body} item xs={12}>
						<DrawerBody asset={asset} product={product} />
					</Grid>
					<Grid container spacing={2} justify="flex-end">
						<Grid item xs={12}>
							<Box pt={1} />
							<Divider variant="fullWidth" />
						</Grid>
					</Grid>
					<Grid container spacing={2} justify="center" alignItems="center">
						<Grid item xs={6}>
							<Box pt={1} />
							<Button className={classes.cancel} startIcon={<BlockIcon />} onClick={handleCancelBtnClick}>
								Close Preview
							</Button>
						</Grid>
						<Grid item xs={6}>
							<Box pt={1} />
							<ManageAssetInNewTabLink linkTxt="Manage" assetUUID={asset.id} icon={<SettingsIcon />} />
						</Grid>
					</Grid>
				</Grid>
			</Drawer>
		</Grid>
	);
};

export default AssetPreviewDrawer;
